import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, HUB_META } from "../lib/api";
import AppShell from "../components/AppShell";
import AvatarUploader from "../components/AvatarUploader";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Buildings } from "@phosphor-icons/react";

export default function OrgNew() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({
    name: "", tagline: "", about: "", website: "", hub_key: "",
    logo_url: "", banner_url: "",
    social: { linkedin: "", x: "", facebook: "", instagram: "", youtube: "" },
    sponsored_agent_keys: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user === false) nav("/login", { state: { from: "/org/new" } });
    api.get("/hubs").then((r) => setHubs(r.data)).catch(() => {});
    api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2) return setErr("Name is required.");
    setBusy(true);
    try {
      const { data } = await api.post("/orgs", form);
      nav(`/org/${data.slug}`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message);
    } finally { setBusy(false); }
  };

  const toggleAgent = (k) => {
    setForm((f) => ({
      ...f,
      sponsored_agent_keys: f.sponsored_agent_keys.includes(k)
        ? f.sponsored_agent_keys.filter((x) => x !== k)
        : [...f.sponsored_agent_keys, k],
    }));
  };

  if (!user || user === false) return <AppShell><div className="py-20 text-center label-mono">...</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="org-new-page">
        <Link to="/orgs" className="btn btn-ghost btn-sm mb-4"><ArrowLeft size={12} weight="bold" /> All organizations</Link>
        <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#E60012" }}>
          <Buildings size={11} weight="fill" /> CREATE ORGANIZATION
        </div>
        <h1 className="font-display text-white mb-8" style={{ fontSize: 40, fontWeight: 900, letterSpacing: "-0.035em" }}>
          Put your org on the map.
        </h1>

        <form onSubmit={submit} data-testid="org-new-form">
          <div className="grid md:grid-cols-[160px_1fr] gap-6 mb-6 items-start">
            <div>
              <div className="label-mono mb-2">LOGO</div>
              <AvatarUploader currentUrl={form.logo_url} onUploaded={(url) => setForm({ ...form, logo_url: url })} size={140} label="Square logo" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">NAME *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} className="input mb-3" placeholder="Acme Mobility, Inc." data-testid="org-new-name" />
              <label className="label-mono block mb-1.5">TAGLINE</label>
              <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} maxLength={200} className="input mb-3" placeholder="Building the propulsion of the next century" data-testid="org-new-tagline" />
              <label className="label-mono block mb-1.5">WEBSITE</label>
              <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input" placeholder="https://acme.com" data-testid="org-new-website" />
            </div>
          </div>

          <label className="label-mono block mb-1.5">ABOUT</label>
          <textarea value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} rows={4} maxLength={4000} className="input mb-6 resize-none" placeholder="What you build, why it matters, who you're hiring..." data-testid="org-new-about" />

          <label className="label-mono block mb-1.5">PRIMARY HUB (OPTIONAL)</label>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-6">
            <button type="button" onClick={() => setForm({ ...form, hub_key: "" })}
              className={`p-2 border text-xs font-mono uppercase ${form.hub_key === "" ? "border-white bg-[#1A1A1D] text-white" : "border-[#27272A] text-zinc-400"}`}
            >ANY</button>
            {hubs.map((h) => {
              const color = HUB_META[h.key]?.color || "#E60012";
              return (
                <button type="button" key={h.key} onClick={() => setForm({ ...form, hub_key: h.key })}
                  className={`p-2 border text-xs ${form.hub_key === h.key ? "border-white bg-[#1A1A1D] text-white" : "border-[#27272A] text-zinc-400"}`}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="hub-dot" style={{ color }} /> {h.name}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="label-mono mb-2">SPONSORED AGENTS</div>
          <p className="text-xs text-zinc-500 mb-3">Pin agents that represent your domain on your org page.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6">
            {agents.map((a) => {
              const on = form.sponsored_agent_keys.includes(a.key);
              return (
                <button type="button" key={a.key} onClick={() => toggleAgent(a.key)}
                  className={`p-2 border text-left ${on ? "border-[#E60012] bg-[#E60012] text-white" : "border-[#27272A] text-zinc-400"}`}
                  data-testid={`org-new-agent-${a.key}`}
                >
                  <div className="text-xs font-mono uppercase">{a.role.replace(" Agent", "")}</div>
                  <div className={`text-xs ${on ? "text-white" : "text-white"}`}>{a.name}</div>
                </button>
              );
            })}
          </div>

          <div className="label-mono mb-2">SOCIAL LINKS</div>
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            {["linkedin", "x", "facebook", "instagram", "youtube"].map((k) => (
              <div key={k}>
                <label className="label-mono block mb-1">{k.toUpperCase()}</label>
                <input value={form.social[k] || ""}
                  onChange={(e) => setForm({ ...form, social: { ...form.social, [k]: e.target.value } })}
                  className="input" placeholder={`https://${k}.com/your-handle`}
                  data-testid={`org-new-social-${k}`}
                />
              </div>
            ))}
          </div>

          {err && <div className="text-xs text-red-500 mb-3 border border-red-900/40 px-3 py-2" data-testid="org-new-error">{err}</div>}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => nav("/orgs")} className="btn btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="org-new-submit">
              {busy ? <span className="spinner" /> : "Create organization"}
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
